import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Minimize2, Maximize2, User } from 'lucide-react';
import { Button } from '../core/button';
import { Input } from '../core/input';
import { Card, CardContent, CardHeader, CardTitle } from '../core/card';
import { useChat } from './chat-context';
import { trpc } from '@org/clients/school';
import { cn } from '../../utils';
import { toast } from 'sonner';

export function ChatWidget() {
  const { isOpen, toggleChat, sessionId, startSession, pageContext } = useChat();
  const [message, setMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const utils = trpc.useUtils();
  
  const historyQuery = trpc.chat.getHistory.useQuery(
    { sessionId: sessionId! },
    { enabled: !!sessionId && isOpen }
  );

  const sendMessageMutation = trpc.chat.sendMessage.useMutation({
    onSuccess: () => {
      setMessage('');
      utils.chat.getHistory.invalidate({ sessionId: sessionId! });
    },
    onError: (error) => {
        toast.error(error.message);
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [historyQuery.data, isOpen, sendMessageMutation.isPending]);

  const handleSend = async () => {
    if (!message.trim() || !sessionId) return;

    sendMessageMutation.mutate({
      sessionId,
      message,
      pageContext: pageContext // Pass current page context
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <Button
        className="fixed bottom-6 right-6 rounded-full h-[4.5rem] w-[4.5rem] shadow-lg z-50 bg-[#ea505c] hover:bg-[#c41a1f] text-white border-2 border-white transition-transform hover:scale-105"
        aria-label="Open chat"
        onClick={() => {
            toggleChat();
            // We let ChatContext handle session initialization/restoration
        }}
      >
        <MessageCircle className="h-12 w-12" strokeWidth={1.8} />
      </Button>
    );
  }

  return (
    <Card className={cn(
      "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 shadow-2xl flex flex-col z-50 transition-all duration-300 bg-background border-border overflow-hidden",
      isExpanded
        ? "w-[80vw] h-[80vh] max-w-4xl"
        : "w-[calc(100vw-2rem)] sm:w-[400px] h-[calc(100vh-2rem)] sm:h-[600px] max-h-[600px]"
    )}>
      <CardHeader className="flex flex-row items-center justify-between py-4 px-4 bg-[#ea505c] text-white rounded-t-lg shadow-md">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center border border-white/30 backdrop-blur-sm overflow-hidden">
             {/* Avatar image or fall back to User icon */}
             <img 
               src="https://storage.googleapis.com/media2empower-latam/public/mellinai-avatar.png" 
               alt="Mellin.AI Avatar"
               className="h-full w-full object-cover"
             />
          </div>
          <div>
            <CardTitle className="text-lg font-bold tracking-wide font-sans">Mellin.AI</CardTitle>
            <p className="text-xs text-white/90 font-medium">Your AI Teacher</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20 rounded-full" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20 rounded-full" onClick={toggleChat}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden bg-slate-50/50">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!sessionId ? (
             <div className="flex justify-center items-center h-full text-muted-foreground animate-pulse text-sm font-medium">
                 Starting session...
             </div>
          ) : historyQuery.isLoading ? (
            <div className="flex justify-center items-center h-full text-muted-foreground animate-pulse text-sm font-medium">
              Loading history...
            </div>
          ) : historyQuery.data?.length === 0 ? (
            <div className="text-center text-muted-foreground mt-8 px-6">
              <div className="h-16 w-16 bg-[#ea505c]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-[#ea505c]" />
              </div>
              <p className="font-medium text-foreground text-lg">👋 Hi! I'm Mellin.AI</p>
              <p className="text-sm mt-2 max-w-[240px] mx-auto leading-relaxed">I'm here to help you with your English learning journey.</p>
              <p className="text-xs mt-4 text-muted-foreground/80">Conversations are saved automatically so you can continue where you left off.</p>
            </div>
          ) : (
            historyQuery.data?.map((msg) => (
              <div
                key={msg.messageId}
                className={cn(
                  "flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm",
                    msg.role === 'user'
                      ? "bg-[#ea505c] text-white rounded-tr-sm"
                      : "bg-white text-foreground border border-border rounded-tl-sm"
                  )}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))
          )}
          {sendMessageMutation.isPending && (
             <div className="flex w-full justify-end animate-in fade-in duration-300">
                <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-3 text-sm bg-[#ea505c] text-white opacity-70 shadow-sm">
                  <p className="whitespace-pre-wrap">{message}</p>
                </div>
             </div>
          )}
           {sendMessageMutation.isPending && (
             <div className="flex w-full justify-start animate-in fade-in duration-300">
                <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-4 py-3 text-sm bg-white border border-border shadow-sm">
                  <div className="flex space-x-1 items-center h-5">
                    <div className="w-2 h-2 bg-[#ea505c] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 bg-[#ea505c] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 bg-[#ea505c] rounded-full animate-bounce"></div>
                  </div>
                </div>
             </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t bg-background">
          <div className="flex gap-2 items-center bg-white rounded-full border border-input px-2 py-1 focus-within:ring-2 focus-within:ring-[#ea505c]/20 transition-all shadow-sm">
            <Input
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={!sessionId || sendMessageMutation.isPending}
              className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2 h-10 placeholder:text-muted-foreground/70"
            />
            <Button 
                onClick={handleSend} 
                disabled={!sessionId || !message.trim() || sendMessageMutation.isPending}
                size="icon"
                className={cn(
                  "h-8 w-8 rounded-full shrink-0 transition-all duration-200",
                  message.trim() ? "bg-[#ea505c] hover:bg-[#c41a1f] text-white shadow-sm transform hover:scale-105 active:scale-95" : "bg-muted text-muted-foreground"
                )}
            >
              <Send className="h-4 w-4 ml-0.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
